import axios from "axios";
import axiosInstance, { axiosFormDataInstance } from "./axiosInstance";
import { Mumeen } from "./mumeenService";
import qs from "qs";

export interface City {
  countryName: string;
  cityId: number | "other";
  cityName: string;
  postalCode?: string;
  countryId: number;
  isActive?: boolean;
}

export interface Country {
  countryId: number;
  countryName: string;
  iso2: string;
  iso3: string;
  dialCode: string | null;
  isActive: boolean;
  cities: City[];
}

export interface DocumentApiResponse {
  documentId: number;
  documentName: string;
  isMandatory: boolean;
  acceptedFormatsList?: string[];
  acceptedFormats?: string;
  maxFileSizeKB?: number | string;
}

export interface DashboardApiResponse {
  total: number;
  pending: number;
  submitted: number;
  approved: number;
  rejected: number;
}

export interface TourOperatorSubmitPayload {
  itsNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  arabicName: string;
  gender: string;
  dateOfBirth: string | null;
  mobileNumber: string;
  alternateMobile: string;
  passportNumber: string;
  officeName: string;
  officeNumber: string;
  businessType: string;
  isAuthorizedGroupLead: boolean;
  loginRequired: boolean;
  countryId: number;
  cityId: string | number;
  otherCity: string;
  permanentAddress: string;
  areasOfOperation: {
    countryId: number;
    cityId: number;
    otherCity: string;
    address: string;
    contactNumber: string;
    isAuthorizedGroupLeader: boolean;
  }[];
  partners: {
    itsNumber: string;
    name: string;
    email: string;
    mobile: string;
    role: "PARTNER" | "OFFICE_STAFF";
    loginRequired: boolean;
    isAuthorizedGroupLeader: boolean;
    // documents: {
    //   documentId: number;
    //   fileName: string;
    // }[];
  }[];
  // documents: {
  //   documentId: number;
  //   fileName: string;
  // }[];
  createdBy: number;
}

export interface TourOperatorRegistrationResponse {
  id: number;
  message: string;
}

export interface AddAreaOfOperationPayload {
  countryId: number;
  cityId: number;
  otherCity?: string;
  address: string;
  contactNumber: string;
  isAuthorizedGroupLeader: boolean;
  tourOperatorId: number;
}

export interface PartnerDocumentPayload {
  partnerDocumentId: number;
  documentId: number;
  fileName: string;
}

export interface AddPartnerPayload {
  itsNumber: string;
  name: string;
  email: string;
  mobile: string;
  role: string; // or "PARTNER" | "OFFICE_STAFF"
  loginRequired: boolean;
  isAuthorizedGroupLeader: boolean;
  documents: PartnerDocumentPayload[];
  tourOperatorId: number;
  isMainOperator: boolean;
}

export interface TourOperatorFilters {
  searchTerm?: string;
  partnerName?: string;
  officeName?: string;
  visaId?: string;
  name?: string;
  country?: string;
  // tourRefNo?: number;
  pageNo?: number;
  submittedpageNo?: number;
  requestMorepageNo?: number;
  pageSize?: number;
  DraftpageSize?: number;
  SubmiitedpageSize?: number;
  RequestMorepageSize?: number;
  itsNumber?: string;
  itsNo?: string;
  category?: string;
  applicationNo?: string;
  applicantName?: string;
  applicationNumber?: string;
  applicantType?: string;
  passportNo?: string;
  nationality?: string;
  status?: string;
  visaRequired?: string;
}

// Helper to get token safely
export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};


interface RegistrationStartResponse {
  registrationToken: string;
  expiresIn: number;
}

export const startRegistration = async (): Promise<string> => {
  const response = await axiosInstance.get<RegistrationStartResponse>(
    "/Registration/start"
  );
  return response.data.registrationToken;
};

export const sendOtpWithITS = async (
  itsNo: string,
  registrationToken: string,
  force = true,
) => {
  try {
    const response = await axiosInstance.post(`/its/send-otp/${itsNo}?force=${force}`,null,
      {
        headers: {
          ...getAuthHeader(),
          "X-Registration-Token": registrationToken,
        },
      },
    );

    return response.data;
  } catch {
    return null;
  }
};

export const verifyOtp = async ({
  itsNumber,
  registrationToken,
  otp,
  force = true,
}: {
  itsNumber: string;
  registrationToken: string;
  otp: string;
  force?: boolean;
}) => {
  const response = await axiosInstance.post(
    `/its/verify-otp?force=${force}`,
    {
      itsNumber,
      otp,
    },
    {
      headers: {
        ...getAuthHeader(),
        "X-Registration-Token": registrationToken,
      },
    },
  );

  return response.data;
};

export const getUserDetailsByITS = async (itsNo: string, force: true) => {
  try {
    const response = await axiosInstance.get(`/its/${itsNo}?force=${force}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch {
    return null;
  }
};

export const getPublicUserDetailsByITS = async (itsNo: string, registrationToken: string, force: true) => {
  try {
    const response = await axiosInstance.get(`/its/${itsNo}?force=${force}`, {
      headers: {
        ...getAuthHeader(),
        "X-Registration-Token": registrationToken,
      },
    });
    return response.data;
  } catch {
    return null;
  }
};

export const ResendOtpWithITS = async (itsNo: string, registrationToken: string,) => {
  try {
    const response = await axiosInstance.post(`/its/resend-otp/${itsNo}`, null, {
      headers: {
        ...getAuthHeader(),
        "X-Registration-Token": registrationToken,
      },
    });
    return response.data;
  } catch {
    return null;
  }
};

// GET ALL THE COUNTRY AND CITIES
export const getCountryCity = async (): Promise<Country[] | null> => {
  try {
    // const response = await axiosInstance.get(`/City/countries-with-cities`);
    const response = await axiosInstance.get(`/City/countries-with-cities`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const checkOfficeNameAvailability = async (
  officeName: string,
): Promise<boolean> => {
  const encodedOfficeName = encodeURIComponent(officeName.trim());
  const response = await axiosInstance.get<{ exists: boolean }>(
    `/tour-operator/exists-by-office-name?OfficeName=${encodedOfficeName}`,
    {
      headers: getAuthHeader(),
    },
  );

  return !response.data.exists;
};

// GET ALL THE COUNTRY AND CITIES
export const getDocuments = async (
  screenKey: string,
): Promise<DocumentApiResponse[] | null> => {
  try {
    const response = await axiosInstance.get(`/Documents/screen/${screenKey}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

//POST SUBMIT TOUR OPERATOR
export const NewTourRegistration = async (
  payload: TourOperatorSubmitPayload,
): Promise<
  | { success: true; data: TourOperatorRegistrationResponse }
  | { success: false; message: string; errors: Record<string, string[]> }
> => {
  try {
    const response = await axiosInstance.post<TourOperatorRegistrationResponse>(
      "/tour-operator/register",
      payload,
    );

    return { success: true, data: response.data };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || "Validation failed",
        errors: error.response.data.errors || {},
      };
    }

    return {
      success: false,
      message: "Something went wrong",
      errors: {},
    };
  }
};

export const NewOneTourRegistration = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post<TourOperatorRegistrationResponse>(
      "/tour-operator/register-new",
      formData,
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return { success: true, data: response.data };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || "Validation failed",
        errors: error.response.data.errors || {},
      };
    }
    return {
      success: false,
      message: "Something went wrong",
      errors: {},
    };
  }
};

export const AddAreaOfOpertaion = async (
  payload: AddAreaOfOperationPayload,
) => {
  try {
    const response = await axiosInstance.post(
      "/tour-operator/area-of-operations",
      payload,
    );

    return { success: true, data: response.data };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || "Validation failed",
        errors: error.response.data.errors || {},
      };
    }

    return {
      success: false,
      message: "Something went wrong",
      errors: {},
    };
  }
};

// TOUR DASHBOARD
export const TourRegistrationDashboard =
  async (): Promise<DashboardApiResponse | null> => {
    try {
      const response = await axiosInstance.get<DashboardApiResponse>(
        "/tour-operator/dashboard",
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
      return null;
    }
  };

export const getRegistrationDetails = async (id: string) => {
  try {
    const response = await axiosInstance.get(
      `/tour-operator/${id}/with-documents`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch dashboard data", error);
    return null;
  }
};

export const updateTourOperatorRegistration = async (
  id: number,
  payload: FormData,
) => {
  const response = await axiosFormDataInstance.put(
    `/tour-operator/${id}`,
    payload,
  );

  return response.data;
};

// TOUR LISTING
export const TourRegistrationListing = async (
  filters: TourOperatorFilters = {},
) => {
  try {
    const response = await axiosInstance.get("/tour-operator/list", {
      params: {
        pageNumber: filters.pageNo ?? 0,
        pageSize: filters.pageSize ?? 20,
        itsNumber: filters.itsNumber || undefined,
        Name: filters.name || "",
        officeName: filters.officeName || "",
        searchText: filters.searchTerm || undefined,
        // StatusCode: filters.status || "Submitted",
        StatusCode:
          filters.status === "Submitted"
            ? ["SUBMITTED", "PENDING"]
            : filters.status
              ? [filters.status.toUpperCase()]
              : undefined,
        CountryId: filters.country || undefined,
        // Nationality: filters.nationality || undefined,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
      headers: {
        ...getAuthHeader(),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// GET DETAILS BY ITS
export const CheckDetailsByITS = async (
  itsNo: string,
): Promise<Mumeen | null> => {
  try {
    const response = await axiosInstance.get<Mumeen>(
      `/tour-operator/partner/${itsNo}`,
      // {
      //   headers: getAuthHeader(),
      // }
    );
    return response.data;
  } catch {
    return null;
  }
};

// GET TOUR-OPERATOR TYPES
export const TourOperatorType = async () => {
  try {
    const response = await axiosInstance.get(`/tour-operator-type`);
    return response.data;
  } catch (error) {
    return null;
  }
};

// ADD NEW PARTNER
export const AddNewPartners = async (
  formData: FormData,
): Promise<
  | { success: true; data: { id: number } }
  | { success: false; message: string; errors: Record<string, string[]> }
> => {
  try {
    const response = await axiosInstance.post(
      "/tour-operator/partners",
      formData,
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return { success: true, data: response.data };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || "Validation failed",
        errors: error.response.data.errors || {},
      };
    }

    return {
      success: false,
      message: "Something went wrong",
      errors: {},
    };
  }
};

//APPROVE NEW PARTNER
export const approvePartner = async (
  tourOperatorId: string,
  partnerId: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/tour-operator/${tourOperatorId}/partner/${partnerId}/approve`,
      {
        headers: {
          ...getAuthHeader(),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to approve partner", error);
    throw error;
  }
};

// DELETE AREA OF OPERATIONS
export const deletePartners = async (
  tourOperatorId: string,
  partnerId: string,
) => {
  try {
    const response = await axiosInstance.delete(
      `/tour-operator/${tourOperatorId}/partner/${partnerId}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete area of operation", error);
    throw error;
  }
};

// DELETE AREA OF OPERATIONS
export const deleteAreaOfOperation = async (
  tourOperatorId: string,
  areaId: string,
) => {
  try {
    const response = await axiosInstance.delete(
      `/tour-operator/${tourOperatorId}/area-of-operation/${areaId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete area of operation", error);
    throw error;
  }
};

export const ApprovedTourOperator = async (
  id: number,
  formData: FormData,
  // rejectedReason: string,
  // userId?: number
) => {
  try {
    const response = await axiosInstance.post(
      `/tour-operator/${id}/approve`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete area of operation", error);
    throw error;
  }
};

export const rejectTourOperator = async (
  id: number,
  rejectedReason: string,
  userId?: number,
) => {
  try {
    const response = await axiosInstance.post(
      `/tour-operator/${id}/reject`,
      rejectedReason,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to reject tour operator", error);
    throw error;
  }
};

// SAVE / UPDATE CONFIGURATION (PUT)
export const saveConfigurations = async (
  tourOperatorId: number,
  partnerId: number,
  payload: {
    tourOperatorId: number;
    partnerId: number;
    permissionExpireDate: string;
    tourOperatorTypeId: number;
    isInclusive: boolean;
    isPublic: boolean;
    permanentDiscount: number;
  },
) => {
  try {
    const response = await axiosInstance.put(
      `/tour-operator/${tourOperatorId}/partner/${partnerId}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json", // ✅ REQUIRED
          ...getAuthHeader(),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to save configuration", error);
    throw error;
  }
};

export const rejectTourOperatorDocument = async (
  id: number,
  rejectedReason: string,
) => {
  try {
    const response = await axiosInstance.post(
      `/documents-central/${id}/reject`,
      // {
      rejectedReason,
      // documentId: id,
      // },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to reject tour operator", error);
    throw error;
  }
};

// RE-UPLOAD-TOUR-DOCUMENT
export const reUploadTourDocuments = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post(
      `/tour-operator/document/re-upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to re-upload tour operator document", error);
    throw error;
  }
};

// RE-UPLOAD-PARTNERS-DOCUMENT
export const reUploadPartnersDocuments = async (formData: FormData) => {
  try {
    const response = await axiosInstance.post(
      `/tour-operator/partner/document/re-upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to re-upload partner document", error);
    throw error;
  }
};
